/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject} from '@angular/core';
import {UIRouterModule} from '@uirouter/angular';

import template from './review-list-page.component.html';
import {SignalAdminListColumn, SignalAdminResourceListStateFactory} from '../common/controller/signal-admin-resource-list-state';
import {deviceTargetSubDevices, hasAmount, previewParams} from './review-list-page-utils';
import {ReviewRejectModalService} from './review-reject-modal.service';
import {LineItemApiService} from '../line-item/line-item-api.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {StateService} from '@uirouter/core';
import {campaignCommons} from '../campaign/campaign-commons';
import {LineItemReviewApiService, LineItemReviewModel} from './line-item-review-api.service';
import {APP_SHARED} from '../common/app-shared';
import {SignalAdminResourceListComponent} from '../common/controller/signal-admin-resource-list.component';
import {CreativeRenderedComponent} from '../creative/ad/creative-rendered.component';
import {LINE_ITEM_LABELS} from '../line-item/line-item-labels';
import {PaymentStatusLabelComponent} from '../payment/payment-status-component';

function enrichReviews(items: LineItemReviewModel[]): LineItemReviewModel[] {
  const host = {items, maxHeight: 100, maxWidth: 100};
  campaignCommons(host).setUpComponents('items');
  return items;
}

@Component({
  standalone: true,
  imports: [
    ...APP_SHARED,
    UIRouterModule,
    SignalAdminResourceListComponent,
    CreativeRenderedComponent,
    ...LINE_ITEM_LABELS,
    PaymentStatusLabelComponent
  ],
  selector: 'adn-review-list-page',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReviewListPageComponent implements OnInit {
  readonly itemStateName = 'app.adv.campaigns.campaign';
  readonly campaignItemStateName = 'app.adv.campaigns.campaign';
  readonly productItemStateName = 'app.adm.products.product';
  readonly orderItemStateName = 'app.adv.orders.order';
  readonly layoutItemStateName = 'app.adm.layouts.layout';
  readonly tierItemStateName = 'app.adm.tiers.tier';
  readonly userItemStateName = 'app.adm.users.user';

  readonly columns: SignalAdminListColumn[] = [
    {param: 'name'},
    {param: 'selfServiceBudget'},
    {param: 'updateTime', label: 'dates'},
    {param: '', label: 'creativeInfo', sortable: false},
    {param: '', label: 'preview', sortable: false}
  ];

  readonly deviceTargetSubDevices = deviceTargetSubDevices;
  readonly hasAmount = hasAmount;
  readonly previewParams = previewParams;
  readonly rowClass = (item: any): Record<string, boolean> => ({rowWarning: !!item.badWords});
  readonly maxWidth = 100;
  readonly maxHeight = 100;
  readonly hasCreativeCopyright = inject(LocalNetworkProfile).isCreativeCopyrightStatus();
  readonly list = inject(SignalAdminResourceListStateFactory).create<LineItemReviewModel>(
    inject(LineItemReviewApiService),
    {
      listId: 'fullReviewList',
      pageSize: 5,
      additionalParams: {excludeIfMissing: 'productId', filterBy: 'userState', filterByValue: 'SUBMITTED'},
      transformResults: enrichReviews
    },
    inject(DestroyRef)
  );

  private readonly lineItemApi = inject(LineItemApiService);
  private readonly $state = inject(StateService);
  private readonly reviewRejectModalService = inject(ReviewRejectModalService);

  ngOnInit(): void {
    this.list.load();
  }

  previewHref(creative: any): string {
    return this.$state.href('nude.preview', previewParams(creative), {absolute: true}) || '';
  }

  reject(item: LineItemReviewModel, creative: any): void {
    this.reviewRejectModalService.open({reviewItem: item, creative, onComplete: () => this.list.load()});
  }

  approve(): void {
    const updates = Object.values(this.list.selected()).map((item) => ({id: item.lineItem.id, userState: 'APPROVED'}));
    this.lineItemApi.bulkSave({}, updates).subscribe(() => {
      this.list.deselectAll();
      this.list.load();
    });
  }
}
